import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { WebsiteDetail } from "@hostdock/types";
import {
  ActionMenu, Badge, Button, ConfirmDialog, DataTable, EmptyState, ErrorState, formatBytes, formatDateTime, formatRelative, KeyValue, Notice, PageHeader,
  Panel, Skeleton, Stat, StatGroup, StatusBadge, Tabs, toast, WebsiteDatabases, WebsiteCron, WebsiteDeployments, WebsiteDomains, WebsiteFiles, WebsiteGit, WebsiteLogs, WebsiteSftp, WordPressManager, WebsiteSettings,
} from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useSession } from "../../session";
import { runtimeLabel, SslBadge, WebsiteStatusBadge } from "./shared";

type Tab = "overview" | "domains" | "databases" | "settings" | "logs" | "deployments" | "placement" | "files" | "sftp" | "cron" | "git" | "wordpress";

export function WebsiteDetailPage() {
  return (
    <RequirePermission anyOf={["WEBSITE_VIEW"]}>
      <WebsiteView />
    </RequirePermission>
  );
}

function WebsiteView() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "overview";
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["website", id],
    queryFn: () => api.websites.get(id),
    refetchInterval: (query) => (["PROVISIONING", "PENDING", "DELETING"].includes(query.state.data?.status ?? "") ? 2000 : 10_000),
  });
  const settingsQ = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const [pending, setPending] = useState<null | "suspend" | "delete">(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["website", id] });
  const jobDone = (msg: string) => { toast.success(msg); setPending(null); refresh(); };
  const suspend = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.websites.suspend(id, reason), onSuccess: () => jobDone("Suspending website") });
  const resume = useMutation({ mutationFn: () => api.websites.resume(id), onSuccess: () => jobDone("Resuming website") });
  const redeploy = useMutation({ mutationFn: () => api.websites.redeploy(id), onSuccess: () => jobDone("Redeploy started") });
  const retry = useMutation({ mutationFn: () => api.websites.retry(id), onSuccess: () => jobDone("Retrying deployment") });
  const action = useMutation({ mutationFn: (a: "restart" | "stop" | "start") => api.websites.action(id, a), onSuccess: (_c, a) => toast.success(`${a.charAt(0).toUpperCase() + a.slice(1)} sent to the node`) });
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: () => api.websites.remove(id, q.data!.primaryDomain), onSuccess: () => { toast.success("Website is being deleted"); navigate("/hosting/websites"); } });

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const w = q.data;
  const manage = can("WEBSITE_MANAGE");
  return (
    <>
      <PageHeader
        title={w ? w.primaryDomain : <Skeleton className="h-6 w-56" />}
        description={w ? `${w.customer.displayName} · ${runtimeLabel(w.runtime, w.runtimeVersion)} · ${w.region.name}${w.server ? ` · ${w.server.name}` : ""}` : undefined}
        meta={w && <div className="flex flex-wrap items-center gap-2"><WebsiteStatusBadge status={w.status} /><SslBadge status={w.ssl} />{w.statusReason && <span className="text-[12.5px] text-muted">{w.statusReason}</span>}</div>}
        actions={w && manage && (
          <>
            {w.status === "FAILED" && <Button variant="primary" loading={retry.isPending} onClick={() => retry.mutate()}>Retry</Button>}
            {w.status === "ACTIVE" && <Button loading={action.isPending} onClick={() => action.mutate("restart")}>Restart</Button>}
            {w.status === "SUSPENDED" && <Button variant="primary" loading={resume.isPending} onClick={() => resume.mutate()}>Resume</Button>}
            <ActionMenu
              trigger={<Button aria-label="More actions">More</Button>}
              actions={[
                { label: "Redeploy", onSelect: () => redeploy.mutate(), hidden: !["ACTIVE", "SUSPENDED", "FAILED"].includes(w.status) },
                { label: "Stop workload", onSelect: () => action.mutate("stop"), hidden: w.status !== "ACTIVE" },
                { label: "Start workload", onSelect: () => action.mutate("start"), hidden: w.status !== "ACTIVE" },
                { label: "Suspend", onSelect: () => setPending("suspend"), hidden: w.status !== "ACTIVE" },
                { label: "Delete website", onSelect: () => setPending("delete"), danger: true, hidden: ["DELETING", "PROVISIONING"].includes(w.status) },
              ]}
            />
          </>
        )}
      />
      <Tabs<Tab>
        value={tab}
        onChange={(t) => setParams({ tab: t }, { replace: true })}
        items={[
          ...(w && (w.appType === "WORDPRESS" || w.appType === "WOOCOMMERCE") ? [{ value: "wordpress" as const, label: "WordPress" }] : []),
          { value: "overview", label: "Overview" }, { value: "domains", label: "Domains" }, { value: "databases", label: "Databases" },
          ...(manage ? [{ value: "files" as const, label: "Files" }, { value: "sftp" as const, label: "SFTP" }] : []),
          { value: "cron", label: "Cron jobs" }, { value: "git", label: "Git" },
          { value: "settings", label: "Settings" },
          ...(can("LOGS_VIEW") || can("WEBSITE_MANAGE") ? [{ value: "logs" as const, label: "Logs" }] : []),
          { value: "deployments", label: "Deployments" },
          ...(can("INFRASTRUCTURE_VIEW") ? [{ value: "placement" as const, label: "Placement" }] : []),
        ]}
      />
      {!w ? <Skeleton className="h-64 w-full" /> : tab === "overview" ? <Overview w={w} />
        : tab === "domains" ? <WebsiteDomains w={w} api={api} canManage={manage} onChange={refresh} />
        : tab === "databases" ? <WebsiteDatabases w={w} api={api} canManage={manage} canManageDatabases={can("DATABASE_MANAGE")} onChange={refresh} />
        : tab === "settings" ? <WebsiteSettings w={w} api={api} canManage={manage} versions={w.runtime === "static" ? [] : (settingsQ.data?.runtimes[w.runtime] ?? [])} onSaved={refresh} />
        : tab === "logs" ? <WebsiteLogs w={w} api={api} />
        : tab === "files" ? <WebsiteFiles w={w} tools={api.tools} canWrite={manage} />
        : tab === "wordpress" ? (
          <WordPressManager w={w} tools={api.tools} canManage={manage} onJob={() => setParams({ tab: "deployments" }, { replace: true })}
            onRedis={manage ? (enabled) => api.websites.redis(w.id, enabled) : undefined} onOpenWebsite={(id) => navigate(`/hosting/websites/${id}`)} />
        )
        : tab === "sftp" ? <WebsiteSftp w={w} tools={api.tools} canManage={manage} />
        : tab === "cron" ? <WebsiteCron w={w} tools={api.tools} canManage={manage} />
        : tab === "git" ? <WebsiteGit w={w} tools={api.tools} canManage={manage} />
        : tab === "deployments" ? <WebsiteDeployments w={w} /> : <Placement w={w} />}
      <ConfirmDialog open={pending === "suspend"} onOpenChange={(o) => !o && setPending(null)} title="Suspend this website?" description="Visitors see a 'temporarily unavailable' page and the workload is stopped. Files and databases are kept." confirmLabel="Suspend" requireReason loading={suspend.isPending} error={suspend.error} onConfirm={(r) => suspend.mutate(r)} />
      <ConfirmDialog open={pending === "delete"} onOpenChange={(o) => !o && setPending(null)} title="Delete this website permanently?" description="The workload, files, the site's databases and its Redis data are destroyed on the node. This cannot be undone." confirmLabel="Delete website" confirmText={w?.primaryDomain} loading={remove.isPending} error={remove.error} onConfirm={() => remove.mutate()} />
    </>
  );
}

function Overview({ w }: { w: WebsiteDetail }) {
  const wl = w.workloads[0];
  return (
    <div className="flex flex-col gap-5">
      {w.status === "FAILED" && <Notice tone="danger" title="Deployment failed">{w.statusReason}</Notice>}
      <StatGroup className="grid-cols-2 lg:grid-cols-5">
        <Stat label="HTTP" value={w.httpStatus ?? "—"} tone={w.httpStatus && w.httpStatus >= 500 ? "danger" : "default"} />
        <Stat label="CPU" value={wl?.cpuPercent != null ? `${wl.cpuPercent}%` : "—"} hint={`package pool ${w.limits.cpuCores} CPU`} />
        <Stat label="Memory" value={wl?.memoryBytes != null ? formatBytes(wl.memoryBytes) : "—"} hint={`package pool ${w.limits.memoryMb} MB`} />
        <Stat label="Storage" value={w.diskBytes != null ? formatBytes(w.diskBytes) : "—"} hint={`plan ${w.limits.storageGb} GB`} />
        <Stat label="Restarts" value={wl?.restartCount ?? "—"} tone={wl && wl.restartCount > 3 ? "warning" : "default"} />
      </StatGroup>
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Workload">
          {wl ? (
            <KeyValue items={[
              { label: "Container", value: <span className="font-mono text-[12.5px]">{wl.containerName}</span> },
              { label: "Image", value: <span className="font-mono text-[12.5px]">{wl.image}</span> },
              { label: "State", value: <StatusBadge status={wl.status === "RUNNING" ? "ACTIVE" : wl.status} /> },
              { label: "Last report", value: wl.lastReportedAt ? formatRelative(wl.lastReportedAt) : "—" },
              { label: "Site account", value: <span className="font-mono text-[12.5px]">{w.systemUser}</span> },
            ]} />
          ) : <EmptyState title="Not deployed yet" />}
        </Panel>
        <Panel title="Services">
          <KeyValue items={[
            { label: "Databases", value: w.databases.length ? w.databases.map((d) => `${d.engine} ${d.name}`).join(", ") : "None" },
            { label: "Redis", value: w.redis ? <span>{w.redis.status} · prefix <code className="font-mono">{w.redis.keyPrefix}</code></span> : "Disabled" },
            { label: "Domains", value: String(w.domains.length) },
            { label: "Inodes", value: w.inodes != null ? w.inodes.toLocaleString() : "—" },
            { label: "Created", value: formatDateTime(w.createdAt) },
          ]} />
        </Panel>
      </div>
    </div>
  );
}

function Placement({ w }: { w: WebsiteDetail }) {
  const p = w.placement as { chosen?: { name: string } | null; manual?: boolean; decidedAt?: string; candidates?: { name: string; eligible: boolean; reasons: string[]; score: number | null; factors: Record<string, number> }[] } | null;
  if (!p?.candidates) return <EmptyState title="Not placed yet" />;
  if (!p.candidates.length) return <Notice tone="warning" title="No eligible node">This region has no servers yet. Add a server to the region, then retry.</Notice>;
  return (
    <div className="flex flex-col gap-4">
      <Notice title={p.chosen ? `Placed on ${p.chosen.name}${p.manual ? " (manual assignment)" : ""}` : "No eligible node"}>
        Decided {formatDateTime(p.decidedAt)}. The scheduler scores CPU, memory, reservations, disk, inodes, load and workload count; CPU alone never decides.
      </Notice>
      <Panel flush>
        <DataTable
          rows={[...p.candidates].sort((a, b) => (b.score ?? -1) - (a.score ?? -1))}
          rowKey={(c) => c.name}
          columns={[
            { key: "n", header: "Node", cell: (c) => <span className="font-mono text-[13px]">{c.name}</span> },
            { key: "e", header: "Eligible", cell: (c) => c.eligible ? <Badge tone="success">Yes</Badge> : <Badge tone="neutral">No</Badge> },
            { key: "s", header: "Score", cell: (c) => <span className="tabular-nums">{c.score ?? "—"}</span> },
            { key: "r", header: "Details", cell: (c) => c.eligible ? <span className="text-[12px] text-muted">{Object.entries(c.factors).map(([k, v]) => `${k} ${(v * 100).toFixed(0)}%`).join(" · ")}</span> : <span className="text-[12px] text-muted">{c.reasons.join("; ")}</span> },
          ]}
        />
      </Panel>
    </div>
  );
}
